/**
 * PnLStatementPanel — Profit & Loss report from the ledger.
 * Visible only to super_admin & chief_accountant (server also enforces).
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { FileDown, RefreshCw, TrendingUp, TrendingDown, BarChart3, PiggyBank } from "lucide-react";
import { inr } from "@/lib/utils-format";

function fyDefaults() {
  const now = new Date();
  const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return {
    date_from: `${fyStartYear}-04-01`,
    date_to: now.toISOString().slice(0, 10),
  };
}

export default function PnLStatementPanel() {
  const def = fyDefaults();
  const [dateFrom, setDateFrom] = useState(def.date_from);
  const [dateTo, setDateTo] = useState(def.date_to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/finance/pnl-statement", { params: { date_from: dateFrom, date_to: dateTo } });
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Only Super Admin and Chief Accountant can view P&L");
      else toast.error("Failed to load P&L");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const download = async (fmt) => {
    try {
      const r = await api.get("/finance/pnl-statement/export", {
        params: { date_from: dateFrom, date_to: dateTo, format: fmt },
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: fmt === "excel" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pnl-${dateFrom}-to-${dateTo}.${fmt === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  if (!data) return <div className="p-6 text-[#8B7355]">Loading P&amp;L…</div>;
  const t = data.totals;

  const Section = ({ title, block, tone }) => (
    <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-hidden">
      <div className={`px-4 py-2 font-[Outfit] font-semibold text-white ${tone === "income" ? "bg-emerald-600" : tone === "direct" ? "bg-red-600" : "bg-[#C05621]"}`}>
        {title}
      </div>
      <table className="w-full text-sm">
        <tbody>
          {block.accounts.map((a) => (
            <tr key={a.code} className="border-t border-[#EAE5D9]">
              <td className="px-6 py-1.5 text-xs"><span className="font-mono text-[#8B7355]">{a.code}</span> · {a.name}</td>
              <td className="px-4 py-1.5 text-right font-mono">{inr(a.amount)}</td>
            </tr>
          ))}
          {block.accounts.length === 0 && (
            <tr><td className="px-4 py-3 text-center text-[#8B7355] italic text-xs" colSpan={2}>No {title.toLowerCase()} in this period</td></tr>
          )}
          <tr className="border-t-2 border-[#2C1810] bg-[#FEF9EA]">
            <td className="px-4 py-2 font-semibold uppercase text-xs">Subtotal — {title}</td>
            <td className="px-4 py-2 text-right font-bold font-mono">{inr(block.subtotal)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const netPositive = t.net_profit >= 0;

  return (
    <div className="pt-4 space-y-4" data-testid="pnl-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-[Outfit] text-2xl font-semibold flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-[#C05621]" /> Profit &amp; Loss Statement
          </h2>
          <p className="text-xs text-[#8B7355]">Revenue − Direct Expenses = Gross Profit &nbsp;·&nbsp; − Indirect Expenses = Net Profit. Rolls up into Balance Sheet Retained Earnings.</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div><Label className="text-xs">From</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" data-testid="pnl-from" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" data-testid="pnl-to" /></div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="pnl-refresh"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
          <Button variant="outline" onClick={() => download("pdf")} data-testid="pnl-pdf"><FileDown className="w-3.5 h-3.5 mr-1" /> PDF</Button>
          <Button variant="outline" onClick={() => download("excel")} data-testid="pnl-excel"><FileDown className="w-3.5 h-3.5 mr-1" /> Excel</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Kpi label="Total Income" value={inr(t.total_income)} tone="green" icon={TrendingUp} testid="pnl-kpi-income" />
        <Kpi label="Direct Expenses" value={inr(t.total_direct_expense)} tone="red" icon={TrendingDown} testid="pnl-kpi-direct" />
        <Kpi label="Gross Profit" sub={`Margin ${t.gross_margin_pct}%`} value={inr(t.gross_profit)} tone={t.gross_profit >= 0 ? "green" : "red"} icon={PiggyBank} testid="pnl-kpi-gross" />
        <Kpi label="Net Profit" sub={`Margin ${t.net_margin_pct}%`} value={inr(t.net_profit)} tone={netPositive ? "green" : "red"} icon={netPositive ? TrendingUp : TrendingDown} testid="pnl-kpi-net" bold />
      </div>

      <div className={`rounded-lg border p-3 flex items-center justify-between ${netPositive ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`} data-testid="pnl-summary">
        <div>
          <div className={`font-[Outfit] font-semibold ${netPositive ? "text-emerald-800" : "text-red-800"}`}>
            {netPositive ? "Profitable Period ✓" : "Loss for the Period"}
          </div>
          <div className="text-xs">
            Period <b>{data.from}</b> to <b>{data.to}</b> · Income <b>{inr(t.total_income)}</b> · Direct <b>{inr(t.total_direct_expense)}</b> · Indirect <b>{inr(t.total_indirect_expense)}</b>
          </div>
        </div>
        <Badge className={`border-0 ${netPositive ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}`}>Net {inr(t.net_profit)}</Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-4">
          <Section title="Revenue" block={data.revenue} tone="income" />
          <Section title="Other Income" block={data.other_income} tone="income" />
        </div>
        <div className="space-y-4">
          <Section title="Direct Expenses (COGS)" block={data.direct_expense} tone="direct" />
          <Section title="Indirect / Operating Expenses" block={data.indirect_expense} tone="indirect" />
        </div>
      </div>

      <div className="bg-[#FEF9EA] border-2 border-[#2C1810] rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <Row label="Total Income" value={inr(t.total_income)} bold />
          <Row label="− Direct Expenses" value={`(${inr(t.total_direct_expense)})`} />
          <Row label="= Gross Profit" value={inr(t.gross_profit)} bold accent={t.gross_profit >= 0 ? "text-emerald-800" : "text-red-800"} />
          <Row label="− Indirect Expenses" value={`(${inr(t.total_indirect_expense)})`} />
          <div />
          <Row label="= Net Profit / (Loss)" value={inr(t.net_profit)} bold big accent={netPositive ? "text-emerald-800" : "text-red-800"} />
        </div>
      </div>
    </div>
  );
}

const Kpi = ({ label, value, sub, tone, icon: Icon, testid, bold }) => {
  const map = {
    green: "bg-emerald-50 border-emerald-200 text-emerald-800",
    red: "bg-red-50 border-red-200 text-red-800",
  };
  return (
    <div className={`rounded-lg border p-4 ${map[tone] || ""}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-80">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className={`font-[Outfit] mt-1 tracking-tight ${bold ? "text-3xl font-bold" : "text-2xl font-semibold"}`}>{value}</div>
      {sub && <div className="text-[10px] uppercase opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
};

const Row = ({ label, value, bold, big, accent }) => (
  <div className="flex items-baseline justify-between border-b border-dashed border-[#EAE5D9] pb-1">
    <span className={`${bold ? "font-semibold" : "text-[#5C4033]"} ${big ? "text-base" : ""}`}>{label}</span>
    <span className={`font-mono ${bold ? "font-bold" : ""} ${big ? "text-lg" : ""} ${accent || ""}`}>{value}</span>
  </div>
);
